import { useEffect, useRef, useState } from 'react';
import { useMoney } from './useMoney';
import { loadBundleAllocations, computeBundleActualCost } from '../services/ItemAllocationService';

export interface SourcedUnits {
  /** Whole sets sourced per bundle id (bottleneck component); absent when
   *  a bundle has no allocations or its load failed. */
  sourced: Readonly<Record<string, number>>;
  loading: boolean;
}

/**
 * Units already sourced for each bundle, read from procurement allocations.
 * Each bundle id is fetched once per mount, in parallel, and never again on
 * re-render. A failed load simply leaves that bundle without a number.
 */
export function useBundleSourcedUnits(bundleIds: readonly string[]): SourcedUnits {
  // Only the number of sets is used, which does not depend on the currency;
  // the project's own currency is passed so nothing here names a currency.
  const { currency } = useMoney();
  const requested = useRef(new Set<string>());
  const [sourced, setSourced] = useState<Record<string, number>>({});
  const [pending, setPending] = useState(0);
  const key = bundleIds.join('|');

  useEffect(() => {
    const fresh = bundleIds.filter(id => !requested.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach(id => requested.current.add(id));
    setPending(n => n + fresh.length);
    fresh.forEach(id => {
      loadBundleAllocations(id)
        .then(rows => {
          const qty = computeBundleActualCost(rows, currency)?.allocatedQuantity;
          if (qty && qty > 0) setSourced(prev => ({ ...prev, [id]: qty }));
        })
        .catch(() => undefined)
        .finally(() => setPending(n => n - 1));
    });
    // bundleIds is represented by `key`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { sourced, loading: pending > 0 };
}
